import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Load the yard's spreadsheet instead of typing 300 tools in by hand.
 * The file is checked first and nothing is written, so the user sees which
 * rows are wrong and decides. Importing only ever adds to what is already
 * in the database — existing entries are skipped or updated, never replaced.
 */
const BulkImportDialog = ({ open, onOpenChange, kind = 'assets', onImported }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('skip');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setDone(null); setMode('skip'); };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/inventory/import/template?kind=${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `expertfix-${kind}-template.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error(t('inv.import.templateFailed')); }
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setDone(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', kind);
      const r = await axios.post(`${API}/inventory/import/preview`, fd);
      setPreview(r.data);
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || t('inv.import.readFailed'));
    }
    setBusy(false);
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      fd.append('mode', mode);
      const r = await axios.post(`${API}/inventory/import/commit`, fd);
      setDone(r.data);
      toast.success(t('inv.import.imported', { created: r.data.created, updated: r.data.updated }));
      onImported?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setBusy(false);
  };

  const s = preview?.summary;
  const badRows = (preview?.rows || []).filter((r) => r.errors.length > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>{t(`inv.import.title.${kind}`)}</DialogTitle>
          <DialogDescription>{t('inv.import.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="mr-2 h-4 w-4" />{t('inv.import.downloadTemplate')}
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
            onChange={pick} data-testid="import-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="choose-file-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            {file ? file.name : t('inv.import.chooseFile')}
          </Button>
        </div>

        {busy && !preview && <p className="text-sm text-muted-foreground">{t('inv.import.checking')}</p>}

        {s && !done && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" data-testid="import-count-new">
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />{t('inv.import.newRows', { count: s.new })}
              </Badge>
              <Badge variant="outline" data-testid="import-count-existing">
                <RefreshCw className="mr-1 h-3.5 w-3.5" />{t('inv.import.existingRows', { count: s.existing })}
              </Badge>
              <Badge variant={s.errors ? 'destructive' : 'outline'} data-testid="import-count-errors">
                <AlertTriangle className="mr-1 h-3.5 w-3.5" />{t('inv.import.errorRows', { count: s.errors })}
              </Badge>
            </div>

            {s.existing > 0 && (
              <div>
                <Label className="text-xs">{t('inv.import.existingHandling')}</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">{t('inv.import.modeSkip')}</SelectItem>
                    <SelectItem value="update">{t('inv.import.modeUpdate')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {badRows.length > 0 && (
              <div className="max-h-60 overflow-y-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">{t('inv.import.row')}</TableHead>
                      <TableHead>{t('inv.import.problem')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {badRows.slice(0, 50).map((r) => (
                      <TableRow key={r.row} data-testid={`import-error-row-${r.row}`}>
                        <TableCell className="font-mono text-xs">{r.row}</TableCell>
                        <TableCell className="text-xs text-destructive">{r.errors.join('; ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <p className="text-xs text-muted-foreground">{t('inv.import.additiveNote')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>{t('common.cancel')}</Button>
              <Button onClick={commit} disabled={busy || (s.new === 0 && !(mode === 'update' && s.existing > 0))}
                data-testid="import-commit-btn">
                <Upload className="mr-2 h-4 w-4" />{t('inv.import.import')}
              </Button>
            </div>
          </div>
        )}

        {done && (
          <div className="space-y-2" data-testid="import-result">
            <p className="text-sm">
              {t('inv.import.resultLine', {
                created: done.created, updated: done.updated,
                skipped: done.skipped, failed: done.failed,
              })}
            </p>
            <div className="flex justify-end">
              <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="import-close-btn">
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
